import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase } from '@fortawesome/pro-solid-svg-icons';
import { connect } from 'react-redux';
import { isBoolean } from 'lodash';

import { isBlank, isPresent } from 'helpers/string_helpers';

import DateWithHover from 'components/timestamps/date_with_hover';
import DocAccessLevelSelect from 'bundles/leads/index/table/cells/doc_access_level';
import { getProject } from 'bundles/project/selectors/index';
import { DOC_ACCESS_LEVEL_FOR_LEASE, DOC_ACCESS_LEVEL_FOR_SALE } from 'bundles/leads/index/table/cell';
import formatPhoneNumber from 'helpers/formatters/advanced_phone_number_formats';

export class LeadInfo extends React.Component {
  static propTypes = {
    features: PropTypes.shape({
      crm: PropTypes.bool
    }).isRequired,
    lead: PropTypes.shape({
      createdAt: PropTypes.date,
      contactCompany: PropTypes.string,
      contactCompanyPath: PropTypes.string,
      contactPath: PropTypes.string,
      contactPhoneNumber: PropTypes.string,
      contactType: PropTypes.string,
      displayName: PropTypes.string.isRequired,
      leadData: PropTypes.shape({
        email: PropTypes.string,
        jobTitle: PropTypes.string,
        phone: PropTypes.string
      }).isRequired,
      marketingPreferences: PropTypes.arrayOf(PropTypes.string),
      updatedAt: PropTypes.date,
      user: PropTypes.shape({
        name: PropTypes.string
      })
    }).isRequired,
    project: PropTypes.shape({
      leaseChecked: PropTypes.bool,
      saleChecked: PropTypes.bool
    })
  };

  render() {
    return (
      <div className="d-flex flex-column gap-4 mb-4">
        {this.renderPersonalInformation()}
        {this.renderJobTitle()}
        {this.renderCompanyRow()}
        {this.renderNewOtherFields()}
        {this.renderViewInDatabase()}
        {this.renderAuditFields()}
      </div>
    );
  }

  renderCompanyRow() {
    const { lead: { contactCompany, displayName } } = this.props;
    if (isBlank(contactCompany) || contactCompany === displayName) return null;

    return (
      <div key="lead-pi-company" name="lead-pi-company">
        <label className="form-label mb-1">Company</label>
        <div>{contactCompany}</div>
      </div>
    );
  }

  renderJobTitle() {
    const jobTitle = this.props.lead.leadData.jobTitle;
    if (isBlank(jobTitle)) return null;

    return (
      <div key="lead-pi-job-title" name="lead-pi-job-title">
        <label className="form-label mb-1">Role / Job title</label>
        <div>{jobTitle}</div>
      </div>
    );
  }

  renderPersonalInformation() {
    // TODO: Fix formatting error caused by phony_formatted.
    // the phone value that comes from leadData is being formatted by phony_formatted in the serializer but it struggles
    // with some of the international numbers that could be input. To fix this we are now using the contactPhoneNumber
    // value that is formatted by the advanced_phone_number_formats helper.
    const { lead: { leadData: { email, phone } } } = this.props;
    const contactPhone = this.props.lead.contactPhoneNumber;

    const emailRow = (isPresent(email) &&
      <div key="lead-pi-email" name="lead-pi-email">
        <label className="form-label mb-1">Email</label>
        <div>
          <a href={`mailto:${email}`}>{email}</a>
        </div>
      </div>
    );

    const phoneRow = (isPresent(phone) &&
      <div key="lead-pi-phone" name="lead-pi-phone">
        <label className="form-label mb-1">Phone Number</label>
        <div>{formatPhoneNumber(contactPhone)}</div>
      </div>
    );

    const rowsToDisplay = [emailRow, phoneRow].filter(Boolean);

    if (rowsToDisplay.length === 0) {
      return null;
    } else {
      return (
        <React.Fragment>
          {rowsToDisplay}
        </React.Fragment>
      );
    }
  }

  renderDocsAccessLevelDropdowns() {
    const { lead, project } = this.props;

    const dropdownForLease = (project.leaseChecked &&
      <div key="lead-pi-doc-access-level-for-lease" name="lead-pi-doc-access-level-for-lease">
        <label className="form-label mb-1">Doc Access Level for Lease</label>
        <DocAccessLevelSelect columnName={DOC_ACCESS_LEVEL_FOR_LEASE} lead={lead}  />
      </div>
    );

    const dropdownForSale = (project.saleChecked &&
      <div key="lead-pi-doc-access-level-for-sale" name="lead-pi-doc-access-level-for-sale">
        <label className="form-label mb-1">Doc Access Level for Sale</label>
        <DocAccessLevelSelect columnName={DOC_ACCESS_LEVEL_FOR_SALE} lead={lead} />
      </div>
    );

    return (
      <React.Fragment>
        {dropdownForLease}
        {dropdownForSale}
      </React.Fragment>
    );
  }

  renderOldOtherFields() {
    const { lead: { listingLabel, is1031Profile }, project: { leaseChecked } } = this.props;
    const is1031Label = isBoolean(is1031Profile) ? (is1031Profile ? 'Yes' : 'No') : null;

    return (
      <React.Fragment>
        {leaseChecked && listingLabel && <div key="lead-pi-space" name="lead-pi-space">
          <label className="form-label mb-1">Space</label>
          <div>{listingLabel}</div>
        </div>}
        <div key="lead-pi-1031-exchange" name="lead-pi-1031-exchange">
          <label className="form-label mb-1">1031 Exchange</label>
          <div>{is1031Label}</div>
        </div>
      </React.Fragment>
    );
  }

  renderMarketingPreferences() {
    const { lead: { marketingPreferences } } = this.props;
    const selectedPrefs = marketingPreferences || [];

    const preferenceLabels = {
      email: 'Email',
      phone: 'Phone Call',
      sms: 'Text'
    };

    const displayPrefs = selectedPrefs
      .map(pref => preferenceLabels[pref])
      .filter(Boolean);

    return (
      <div key="lead-pi-marketing-prefs" name="lead-pi-marketing-prefs">
        <label className="form-label mb-1">Marketing Preferences</label>
        <div>{displayPrefs.length > 0 ? displayPrefs.join(', ') : '-'}</div>
      </div>
    );
  }

  renderNewOtherFields() {
    const { lead: { is1031Profile, oneTimeLinkSentAt } } = this.props;
    const is1031Label = isBoolean(is1031Profile) ? (is1031Profile ? 'Yes' : 'No') : '-';
    const lastLinkSentAt = oneTimeLinkSentAt ? <DateWithHover timestamp={oneTimeLinkSentAt} /> : '-';

    return (
      <React.Fragment>
        <div>
          <label className="form-label mb-1">Invite to View Sent</label>
          <div>{lastLinkSentAt}</div>
        </div>
        {this.renderMarketingPreferences()}
        <div key="lead-pi-1031-exchange" name="lead-pi-1031-exchange">
          <label className="form-label mb-1">1031 Exchange</label>
          <div>{is1031Label}</div>
        </div>
      </React.Fragment>
    );
  }

  renderViewInDatabase() {
    const { features, lead } = this.props;
    if (isBlank(lead.contactPath) || !features.crm) return null;

    const links = [];

    if (lead.contactPath) {
      links.push(
        <div key="1">
          <a href={lead.contactPath} name="lead-view-in-database" rel="noopener noreferrer" target="_blank">
            <FontAwesomeIcon className="me-2 text-muted" icon={faDatabase} />
            View {lead.displayName}
          </a>
        </div>
      );
    }

    if (lead.contactCompany && lead.contactCompanyPath && !lead.contactType === 'Contacts::Company') {
      links.push(
        <div key="2">
          <a href={lead.contactCompanyPath} rel="noopener noreferrer" target="_blank">
            <FontAwesomeIcon className="me-2 text-muted" icon={faDatabase} />
            View {lead.contactCompany}
          </a>
        </div>
      );
    }

    if (links.length === 0) return null;

    return (
      <React.Fragment>
        <div key="lead-pi-database" name="lead-pi-database" >
          <label className="form-label mb-1">Database</label>
          {links}
        </div>
      </React.Fragment>
    );
  }

  createdByUserName() {
    const { lead: { source, user } } = this.props;

    if (source === 'web_activity') return '';
    return `by ${user.name}`;
  }

  renderAuditFields() {
    const { lead: { createdAt, updatedAt } } = this.props;

    return (
      <React.Fragment>
        <div key="lead-pi-updated-at" name="lead-pi-updated-at">
          <label className="form-label mb-1">Last Updated</label>
          <div><DateWithHover timestamp={updatedAt}/></div>
        </div>
        <div key="lead-pi-created-at" name="lead-pi-created-at">
          <label className="form-label mb-1">Date Added</label>
          <div><DateWithHover timestamp={createdAt}/> {this.createdByUserName()}</div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  features: state.features,
  project: getProject(state),
});

export default connect(mapStateToProps)(LeadInfo);
